import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import MatchCard from "./MatchCard";
import StatusBadge from "../ui/StatusBadge";
import matchService from "../../services/api/matchService";
import LiveMatch from "./live/LiveMatch";
import { eid } from "../../utils/entityId";
import FilterBar from "../ui/FilterBar";
import { useUserFilters } from "../../contexts/UserFiltersContext";
import "../../styles/features/matches.css";
import { eventTimeZone, formatEventTime } from "../../utils/eventTime";

const PAGE_SIZE = 50;
const LIVE_STATUSES = ["in_progress", "paused"];
// The tab a status filter belongs to
// An expired match (left open, no result) is over: it sits in the Completed tab
const viewOfStatus = (status) => (["completed", "cancelled", "expired"].includes(status) ? "completed" : "upcoming");
// The API sends scheduledAt; older callers and tests use scheduled_at
const scheduledAtOf = (m) => m.scheduledAt || m.scheduled_at;

/** Upcoming & live in sections: live, then awaiting a result (time passed), then upcoming. */
const feedSections = (items, view, now = new Date()) => {
  if (view === "completed") {
    return [{ key: "completed", title: null, items }];
  }
  const live = items.filter((m) => LIVE_STATUSES.includes(m.status));
  const rest = items.filter((m) => !LIVE_STATUSES.includes(m.status));
  const awaiting = rest.filter((m) => scheduledAtOf(m) && new Date(scheduledAtOf(m)) < now);
  const upcoming = rest.filter((m) => !awaiting.includes(m));
  return [
    { key: "live", title: "🔴 Live", items: live },
    { key: "awaiting", title: "⏳ Awaiting result", items: awaiting },
    { key: "upcoming", title: "📅 Upcoming", items: upcoming },
  ].filter((s) => s.items.length);
};

const MatchList = ({
  tournamentId: propTournamentId,
  onMatchSelect,
  allowScoring = false,
  token,
  currentUser,
  onLogout,
}) => {
  const { tournamentId: urlTournamentId } = useParams();
  const navigate = useNavigate();
  const tournamentId = propTournamentId || urlTournamentId;
  const { filterParams, autoExpandRadius, isFiltersReady, needsLocation } = useUserFilters();
  const [matches, setMatches] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  // ?status=in_progress (the dashboard's Live Matches tile, DB-B02) preselects the filter
  const [searchParams] = useSearchParams();
  const initialStatus = ["scheduled", "in_progress", "completed", "cancelled", "expired"].includes(searchParams.get("status"))
    ? searchParams.get("status")
    : "all";
  const [filters, setFilters] = useState({
    search: "",
    status: initialStatus,
    sport: "",
    venue: "",
  });
  const [viewMode, setViewMode] = useState("modern"); // 'modern' or 'legacy'
  // The global feed: Upcoming & live or Completed, paged by the server (PR 6)
  const [feedView, setFeedView] = useState(viewOfStatus(initialStatus));
  const [feed, setFeed] = useState({ items: [], total: 0, counts: { upcoming: 0, completed: 0 } });
  const loadedRef = useRef(0); // matches shown in the feed: the next page's offset
  const [loadingMore, setLoadingMore] = useState(false);

  // Derived filter options — built from whatever is actually in the loaded data
  const loadedMatches = useMemo(
    () => (tournamentId ? Object.values(matches).flat() : feed.items),
    [tournamentId, matches, feed.items]
  );

  const availableSports = useMemo(() => {
    const names = new Set();
    loadedMatches.forEach((m) => { if (m.sport_name) names.add(m.sport_name); });
    return Array.from(names).sort();
  }, [loadedMatches]);

  const availableVenues = useMemo(() => {
    const venues = new Set();
    loadedMatches.forEach((m) => { if (m.venue) venues.add(m.venue); });
    return Array.from(venues).sort();
  }, [loadedMatches]);

  const loadMatches = useCallback(async ({ append = false } = {}) => {
    // When not scoped to a tournament, wait for sport filters to be ready
    if (!tournamentId && !isFiltersReady) return;
    // No location yet: the FilterBar asks for one; never list every region
    if (!tournamentId && needsLocation) {
      setFeed({ items: [], total: 0, counts: { upcoming: 0, completed: 0 } });
      loadedRef.current = 0;
      setLoading(false);
      return;
    }
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = {};
      // Apply filters
      if (filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.sport) {
        params.sport = filters.sport;
      }
      if (filters.venue) {
        params.venue = filters.venue;
      }

      if (tournamentId) {
        const response = await matchService.getTournamentMatches(
          tournamentId,
          params
        );
        // Group matches by round for the tournament view
        const groupedMatches = {};
        (response.data || []).forEach((match) => {
          const round = match.round || 1;
          if (!groupedMatches[round]) {
            groupedMatches[round] = [];
          }
          groupedMatches[round].push(match);
        });
        setMatches(groupedMatches);
      } else {
        // Merge global filters (sport + location); the server orders and pages the feed
        const offset = append ? loadedRef.current : 0;
        const response = await matchService.getAllMatches({
          ...filterParams, ...params, view: feedView, limit: PAGE_SIZE, offset,
        });
        const data = response.data || {};
        const page = data.matches || [];
        loadedRef.current = offset + page.length;
        setFeed((prev) => {
          const items = append ? [...prev.items, ...page] : page;
          return {
            items,
            total: data.total ?? items.length,
            counts: data.counts || { upcoming: 0, completed: 0 },
          };
        });
        // Auto-expand radius if fewer than 5 results due to location filter
        if (filterParams.lat != null && !append) autoExpandRadius(page.length);
      }
      setError(null);
    } catch (err) {
      console.error("Error loading matches:", err);
      // On error, show empty state instead of blocking error screen
      if (!append) {
        setMatches({});
        setFeed({ items: [], total: 0, counts: { upcoming: 0, completed: 0 } });
        loadedRef.current = 0;
      }
      setError(null);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tournamentId, filters, filterParams, isFiltersReady, needsLocation, feedView]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  // Filter handlers
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: newSearch,
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    // A status lives in one tab: picking it opens that tab
    if (key === "status" && value !== "all") {
      setFeedView(viewOfStatus(value));
    }
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      sport: "",
      venue: "",
    });
  };

  const handleMatchClick = (match) => {
    if (onMatchSelect) {
      onMatchSelect(match);
    } else {
      setSelectedMatch(match);
    }
  };

  const handleViewMatch = (match) => {
    // Navigate to match details or live match based on status
    if (match.status === 'in_progress') {
      navigate(`/matches/${eid(match)}/live`);
    } else {
      navigate(`/matches/${eid(match)}`);
    }
  };

  const handleStartMatch = async (match) => {
    try {
      // Navigate to live match interface
      navigate(`/matches/${eid(match)}/live`);
    } catch (error) {
      console.error('Error navigating to match:', error);
    }
  };

  if (loading) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <FloatingCard className="loading-card-gaming">
          <div className="loading-content-gaming">
            <div className="gaming-spinner"></div>
            <h3 className="loading-title">⚽ Loading Matches</h3>
            <p className="loading-text">
              Fetching the latest match information...
            </p>
          </div>
        </FloatingCard>
      </GameLayout>
    );
  }

  if (error) {
    return (
      <GameLayout
        token={token}
        onLogout={onLogout}
        profile={currentUser}
        showFloatingNav={true}
      >
        <FloatingCard className="error-card-gaming">
          <div className="error-content-gaming">
            <span className="error-icon">⚠️</span>
            <h3 className="error-title">Something went wrong</h3>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <FloatingButton variant="primary" onClick={loadMatches} icon="🔄">
                Try Again
              </FloatingButton>
              <FloatingButton
                variant="secondary"
                onClick={() => navigate("/dashboard")}
                icon="←"
              >
                Back to Dashboard
              </FloatingButton>
            </div>
          </div>
        </FloatingCard>
      </GameLayout>
    );
  }

  const matchRounds = Object.keys(matches).sort(
    (a, b) => parseInt(a) - parseInt(b)
  );

  // Tournament view: one section per round; the feed: live, awaiting result, upcoming
  const sections = tournamentId
    ? matchRounds.map((round) => ({ key: round, title: null, items: matches[round] }))
    : feedSections(feed.items, feedView);

  const totalMatches = tournamentId
    ? Object.values(matches).reduce((sum, round) => sum + round.length, 0)
    : feed.total;

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={currentUser}
      showFloatingNav={true}
    >
      <div className="match-list-gaming">
        {/* Gaming Header */}
        <FloatingCard className="match-header-gaming delay-1">
          <div className="match-header-content">
            <div className="header-left">
              <h1 className="match-title-gaming">
                {tournamentId ? "🏆 Tournament Matches" : "Matches"}
              </h1>
              <p className="match-subtitle-gaming">
                {totalMatches} {totalMatches === 1 ? "match" : "matches"} found
              </p>
            </div>
            <div className="header-actions-gaming" style={{ marginLeft: 'auto' }}>
              <FloatingButton
                variant="success"
                size="large"
                onClick={() => navigate("/matches/create")}
                icon="⚽"
                className="delay-2"
              >
                <span className="btn-text">Create Match</span>
              </FloatingButton>
            </div>
          </div>
        </FloatingCard>

        {/* Pending Challenges – only shown for non-tournament views */}


        {/* Compact Search, Filters & Results — FilterBar embedded for non-tournament views */}
        <FloatingCard className="search-controls-compact">
          {!tournamentId && <FilterBar inline showSports={false} />}
          {!tournamentId && (
            <div className="match-feed-tabs" role="group" aria-label="Matches view">
              {[
                ["upcoming", "Upcoming & live"],
                ["completed", "Completed"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`match-feed-tab${feedView === key ? " is-active" : ""}`}
                  aria-pressed={feedView === key}
                  onClick={() => setFeedView(key)}
                >
                  {label} ({feed.counts[key] ?? 0})
                </button>
              ))}
            </div>
          )}
          <div className="controls-row-compact">
            {/* Search Section */}
            <div className="search-section-compact">
              <div className="search-input-wrapper-compact">
                <FaSearch className="search-icon-compact" />
                <input
                  type="text"
                  placeholder="Search matches..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  className="search-input-compact"
                />
              </div>
            </div>

            {/* Inline Filters */}
            <div className="inline-filters-compact">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="filter-select-compact"
              >
                <option value="all">All Matches</option>
                <option value="scheduled">📅 Scheduled</option>
                <option value="in_progress">🔴 Live</option>
                <option value="completed">✅ Completed</option>
                <option value="cancelled">❌ Cancelled</option>
                <option value="expired">⌛ Expired, no result</option>
              </select>

              <select
                value={filters.sport}
                onChange={(e) => handleFilterChange("sport", e.target.value)}
                className="filter-select-compact"
              >
                <option value="">All Sports</option>
                {availableSports.map((sport) => (
                  <option key={sport} value={sport}>
                    {sport}
                  </option>
                ))}
              </select>

              <select
                value={filters.venue}
                onChange={(e) => handleFilterChange("venue", e.target.value)}
                className="filter-select-compact"
              >
                <option value="">All Venues</option>
                {availableVenues.map((venue) => (
                  <option key={venue} value={venue}>
                    📍 {venue}
                  </option>
                ))}
              </select>

              {(filters.search ||
                filters.status !== "all" ||
                filters.sport ||
                filters.venue) && (
                <FloatingButton
                  variant="warning"
                  size="small"
                  onClick={clearFilters}
                  icon="🧹"
                >
                  <span className="btn-text">Clear Filters</span>
                </FloatingButton>
              )}
            </div>

            {/* Results Count & Actions */}
            <div className="results-actions-compact">
              <div className="results-count-compact">
                <span className="count-text-compact">
                  <strong>{totalMatches}</strong>{" "}
                  {totalMatches === 1 ? "match" : "matches"}
                </span>
              </div>

              <div className="view-actions-compact">
                <button
                  onClick={() => setViewMode("modern")}
                  className={`view-btn-compact ${viewMode === "modern" ? "active" : ""}`}
                  title="Card view"
                >
                  🎨
                </button>
                <button
                  onClick={() => setViewMode("legacy")}
                  className={`view-btn-compact ${viewMode === "legacy" ? "active" : ""}`}
                  title="List view"
                >
                  📋
                </button>
                <button
                  onClick={loadMatches}
                  className="refresh-btn-compact"
                  title="Refresh matches"
                >
                  🔄
                </button>
              </div>
            </div>
          </div>
        </FloatingCard>

        {/* Match Content */}
        {sections.length === 0 ? (
          <FloatingCard className="empty-state-gaming delay-3">
            <div className="empty-content-gaming">
              <div className="empty-icon">🏟️</div>
              <h2 className="empty-title">{!tournamentId && needsLocation ? "Matches near you" : "No matches found"}</h2>
              <p className="empty-description">
                {tournamentId
                  ? "No matches have been created for this tournament yet."
                  : needsLocation
                  ? "Set your location above to see what's near you."
                  : "No matches match your current filter criteria."}
              </p>
              <div className="empty-actions">
                <FloatingButton
                  variant="primary"
                  size="medium"
                  onClick={() => navigate("/matches/create")}
                  icon="⚽"
                >
                  Create Your First Match
                </FloatingButton>
                {(filters.search ||
                  filters.status !== "all" ||
                  filters.sport ||
                  filters.venue) && (
                  <FloatingButton
                    variant="secondary"
                    onClick={clearFilters}
                    icon="👁️"
                  >
                    Show All Matches
                  </FloatingButton>
                )}
              </div>
            </div>
          </FloatingCard>
        ) : (
          <div className="matches-content-gaming">
            {sections.map((section) => (
              <section key={section.key} aria-label={section.title || undefined}>
              {section.title && <h3 className="match-feed-section__title">{section.title}</h3>}
              <div className={`matches-grid-gaming ${viewMode === "modern" ? "cards-view" : "list-view"}`}>
                {section.items.map((match) =>
                  viewMode === "modern" ? (
                    <MatchCard
                      key={match.id}
                      match={match}
                      onViewMatch={() => handleViewMatch(match)}
                      onStartMatch={() => handleStartMatch(match)}
                      allowScoring={allowScoring}
                    />
                  ) : (
                    // Compact list-view row
                    <div
                      key={match.id}
                      className="match-list-row"
                      onClick={() => handleMatchClick(match)}
                    >
                      <div className="mlr-status">
                        <StatusBadge status={match.status} kind="match" />
                      </div>

                      <div className="mlr-id">#{match.match_number || match.id}</div>

                      <div className="mlr-participants">
                        <span
                          className="mlr-p1"
                          style={match.participant_1_type === 'user' && match.participant_1_id ? { cursor: 'pointer', color: '#b8acff' } : {}}
                          onClick={match.participant_1_type === 'user' && match.participant_1_id ? (e) => { e.stopPropagation(); navigate(`/players/${match.participant_1_id}`); } : undefined}
                        >
                          {match.participant_1_name || match.participants?.[0]?.name || "TBD"}
                        </span>
                        <span className="mlr-score">
                          {match.score_data?.participant1?.total ?? 0}
                          {" — "}
                          {match.score_data?.participant2?.total ?? 0}
                        </span>
                        <span
                          className="mlr-p2"
                          style={match.participant_2_type === 'user' && match.participant_2_id ? { cursor: 'pointer', color: '#b8acff' } : {}}
                          onClick={match.participant_2_type === 'user' && match.participant_2_id ? (e) => { e.stopPropagation(); navigate(`/players/${match.participant_2_id}`); } : undefined}
                        >
                          {match.participant_2_name || match.participants?.[1]?.name || "TBD"}
                        </span>
                      </div>

                      <div className="mlr-meta">
                        {match.sport_name && <span className="mlr-tag mlr-sport">{match.sport_name}</span>}
                        {match.venue     && <span className="mlr-tag mlr-venue">📍 {match.venue}</span>}
                        {scheduledAtOf(match) && (
                          <span className="mlr-tag mlr-date">
                            {formatEventTime(scheduledAtOf(match), eventTimeZone(match), { year: "numeric", month: "numeric", day: "numeric" })}
                          </span>
                        )}
                      </div>

                      <div className="mlr-actions" onClick={(e) => e.stopPropagation()}>
                        {allowScoring && match.status === "scheduled" && (
                          <button
                            className="mlr-btn mlr-btn--start"
                            onClick={() => handleStartMatch(match)}
                          >
                            ▶ Start
                          </button>
                        )}
                        <button
                          className="mlr-btn mlr-btn--view"
                          onClick={() => handleViewMatch(match)}
                        >
                          View &#x2192;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {!tournamentId && feed.items.length < feed.total && (
              <div className="match-feed-more">
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => loadMatches({ append: true })}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : `Load more (${feed.total - feed.items.length} left)`}
                </FloatingButton>
              </div>
            )}
          </div>
        )}

        {/* Live Match Modal */}
        {selectedMatch && (
          <LiveMatch
            matchId={selectedMatch.id}
            onClose={() => setSelectedMatch(null)}
            isReadOnly={!allowScoring}
            token={token}
            currentUser={currentUser}
            onLogout={onLogout}
          />
        )}
      </div>
    </GameLayout>
  );
};

export default MatchList;
